import React from 'react';
import _ from 'lodash';
import { AnalyticsReport } from 'store/analytics/ui/analyticsUiTypes';
import HelpTooltip from 'components/overlays/helpTooltip/HelpTooltip';
import BzmIcon from 'components/icons/BzmIcon';
import { Button, ButtonGroup, Col, Dropdown, Row } from 'react-bootstrap';
import {
  analyticsReportTypes,
  MOCK_SERVICE_RESPONSE_TIME,
  TRANSACTION_DAILY_COUNTS,
  TRANSACTION_HITS_AND_MISSES,
} from 'store/analytics/analyticsReportTypes';
import { useTranslation } from 'react-i18next';
import { MockServiceAnalytics } from '../../../../../types/rest/mockServiceTypes';

export type ExportType = 'csvExport' | 'pdfExport' | 'reportUrlExport' | 'reportLinkExport' | 'pngExport';

type ReportHeaderProps = {
  /**
   * Selected report
   */
  selectedReport: AnalyticsReport;

  /**
   * Indicates whether it is possible to change reports
   */
  isReportSelectable: boolean;

  /** Indicates if reports are being loaded */
  isLoading: boolean;

  /**
   * Currently selected virtual service
   * @param mockService
   */
  mockService: MockServiceAnalytics;

  /**
   * Select report handler.
   * @param report
   */
  onSelectReport?: (report: AnalyticsReport) => void;

  refreshAction: () => void;
  exportAction: (type: ExportType) => void;
  setAggregation: (report: AnalyticsReport, value: string) => void;
  selectedResolution: string;
};

const ReportHeader = (props: ReportHeaderProps) => {
  const { t } = useTranslation('analytics');

  const chevron = <BzmIcon variant={'icon-bzm-chevron-down'} className="ml-2"></BzmIcon>;
  const helpId = props.mockService.type === 'SV' ? 'ms-analytics-help' : 'analytics-transaction-help';
  const helpIcon = (
    <HelpTooltip id={helpId} className="ml-3" fontSize={22} />
  );

  // Aggregation buttons
  const createAggregationButtons = () => {
    const aggregationList: any[] = [];
    const report: AnalyticsReport = props.selectedReport;
    switch (report.id) {
      case TRANSACTION_HITS_AND_MISSES:
        aggregationList.push({
          type: 'byDay',
          value: '1D',
          title: 'daily',
          selected: props.selectedResolution === '1D' ? 'primary' : 'secondary',
        });
        aggregationList.push({
          type: 'byWeek',
          value: '1W',
          title: 'weekly',
          selected: props.selectedResolution === '1W' ? 'primary' : 'secondary',
        });
        aggregationList.push({
          type: 'byMonth',
          value: '1M',
          title: 'monthly',
          selected: props.selectedResolution === '1M' ? 'primary' : 'secondary',
        });
        break;
      case TRANSACTION_DAILY_COUNTS:
        // aggregationList.push({type: 'By Virtual Service', value:''});
        // aggregationList.push({type: 'By Day', value:''});
        break;
      case MOCK_SERVICE_RESPONSE_TIME:
        // aggregationList.push({type: 'Maximum', value:''});
        // aggregationList.push({type: 'Average', value:''});
        // aggregationList.push({type: 'Minimum', value:''});
        break;
    }

    return (
      <div className="align-middle mt-3">
        <ButtonGroup>
          {aggregationList.map(aggr => (
            <Button
              key={aggr.value}
              onClick={() => props.setAggregation(report, aggr.value)}
              variant={aggr.selected}
              title={t('reports.header.aggregations.' + aggr.title)}
              style={{ height: '30px' }}
              disabled={props.isLoading}
            >
              <span className="ml-2">{t('reports.header.aggregations.' + aggr.type)}</span>
            </Button>
          ))}
        </ButtonGroup>
      </div>
    );
  };

  // Action button icons
  const createActionIconsDom = () => {
    return (
      <div className="float-right align-middle mt-3">
        <Button
          onClick={() => props.refreshAction()}
          variant="primary"
          title={t('reports.header.refreshTitle')}
          disabled={props.isLoading}
        >
          <BzmIcon variant="icon-bzm-sync"></BzmIcon>
          <span className="ml-2">{t('reports.header.refresh')}</span>
        </Button>

        <Dropdown className="d-inline-block">
          <Dropdown.Toggle id="change-report" as={ReportExportToggle}></Dropdown.Toggle>
          <Dropdown.Menu alignRight={true} role="menu">
            {createExportItemsDom()}
          </Dropdown.Menu>
        </Dropdown>
      </div>
    );
  };

  // Export report items
  const availableExportTypes: ExportType[] = ['pngExport', 'pdfExport'];
  const createExportItemsDom = () => {
    return availableExportTypes.map(exportType => (
      <Dropdown.Item key={exportType} onClick={() => props.exportAction(exportType)}>
        {t('reports.header.exportTypes.' + exportType)}
      </Dropdown.Item>
    ));
  };

  const ReportExportToggle = React.forwardRef(({ onClick }: any, ref: any) => (
    <Button
      className="ml-4 align-middle"
      variant="secondary"
      title={t('reports.header.exportTitle')}
      style={{ height: '30px' }}
      ref={ref}
      disabled={props.isLoading}
      onClick={(e: any) => {
        e.preventDefault();
        onClick(e);
      }}
    >
      <BzmIcon variant="icon-bzm-ct-export"></BzmIcon>
      <span className="ml-2">{t('reports.header.export')}</span>
    </Button>
  ));

  // Mandatory attribute for BootstrapToggle
  ReportExportToggle.displayName = 'ReportToggle';

  const getReportDropdownItems = () => {
    // We can divide reports by type here if needed...
    // if (props.mockService.type === 'TRANSACTIONAL') {}
    return createReportDropdownItems(analyticsReportTypes);
  };

  // Report items
  const createReportDropdownItems = (analyticsReports: AnalyticsReport[]) => {
    return analyticsReports.map(report => (
      <Dropdown.Item key={report.id} onClick={() => (props.onSelectReport ? props.onSelectReport(report) : _.noop())}>
        {report.name}
      </Dropdown.Item>
    ));
  };

  const ReportToggle = React.forwardRef(({ onClick }: any, ref: any) => (
    <h2
      onClick={e => {
        e.preventDefault();
        onClick(e);
      }}
      ref={ref}
      className="d-inline-block mb-2 hand-cursor"
    >
      <a className="btn-link-black" href="#" style={{ color: 'inherit', textDecoration: 'none' }}>
        {props.selectedReport.name}
      </a>
      {chevron}
      {helpIcon}
    </h2>
  ));

  // Mandatory attribute for BootstrapToggle
  ReportToggle.displayName = 'ReportToggle';

  return (
    <div className="mb-5">
      <Row>
        <Col lg={6}>
          {/* Report Dropdown */}
          <Dropdown>
            <Dropdown.Toggle id="change-report" as={ReportToggle}></Dropdown.Toggle>
            <Dropdown.Menu role="menu">{getReportDropdownItems()}</Dropdown.Menu>
          </Dropdown>
          {/* ./ Report Dropdown */}

          <p className="mt-1">
            {t('reports.' + props.selectedReport.id + '.description')} {t('reports.utcNote')}
          </p>
        </Col>

        <Col lg={3}>
          {/* Aggregation */}
          {createAggregationButtons()}
          {/* ./Aggregation */}
        </Col>

        <Col lg={3}>
          {/* Buttons */}
          {createActionIconsDom()}
          {/* ./Buttons */}
        </Col>
      </Row>
    </div>
  );
};

export default ReportHeader;
